'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface TrustIndicator {
  id: string;
  icon: string;
  label: string;
  value: string;
  description: string;
  link?: string;
}

interface TrustIndicatorBarProps {
  isVisible?: boolean;
}

const TrustIndicatorBar = ({ isVisible = true }: TrustIndicatorBarProps) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [showBar, setShowBar] = useState(false);

  const trustIndicators: TrustIndicator[] = [
    {
      id: 'hospitals',
      icon: 'BuildingOffice2Icon',
      label: 'Hospital Partners',
      value: '40+',
      description: 'Leading children\'s hospitals and medical centers',
      link: '#implementation',
    },
    {
      id: 'success',
      icon: 'ChartBarIcon',
      label: 'Success Rate',
      value: '94%',
      description: 'Improved clinical outcomes in neonatal brain complications',
      link: '#clinical-evidence',
    },
    {
      id: 'patients',
      icon: 'HeartIcon',
      label: 'Patients Treated',
      value: '400+',
      description: 'Neonates successfully treated with REVIVE technology',
      link: '#device-technology',
    },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setShowBar(window.scrollY > 100);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (!isHovered) {
      const interval = setInterval(() => {
        setCurrentIndex((prev) => (prev + 1) % trustIndicators.length);
      }, 4000);

      return () => clearInterval(interval);
    }
  }, [isHovered, trustIndicators.length]);

  const handleIndicatorClick = (link?: string) => {
    if (link) {
      const element = document.querySelector(link);
      if (element) {
        const offsetTop = element.getBoundingClientRect().top + window.pageYOffset - 80;
        window.scrollTo({
          top: offsetTop,
          behavior: 'smooth',
        });
      }
    }
  };

  if (!isVisible || !showBar) {
    return null;
  }

  const currentIndicator = trustIndicators[currentIndex];

  return (
    <div className="fixed top-24 left-0 right-0 z-[90] bg-success text-success-foreground shadow-md">
      <div className="max-w-[1200px] mx-auto px-6">
        <div
          className="flex items-center justify-between py-3"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <div className="flex items-center space-x-8 flex-1">
            <button
              onClick={() => handleIndicatorClick(currentIndicator.link)}
              className="flex items-center space-x-3 hover:opacity-80 transition-opacity duration-250 ease-out"
            >
              <div className="p-2 bg-success-foreground/20 rounded-lg">
                <Icon name={currentIndicator.icon as any} size={20} className="text-success-foreground" />
              </div>
              <div className="text-left">
                <div className="flex items-center space-x-2">
                  <span className="text-sm font-medium">{currentIndicator.label}</span>
                  <span className="text-lg font-bold font-heading">{currentIndicator.value}</span>
                </div>
                <p className="text-xs opacity-90">{currentIndicator.description}</p>
              </div>
            </button>
          </div>

          <div className="hidden md:flex items-center space-x-2">
            {trustIndicators.map((indicator, index) => (
              <button
                key={indicator.id}
                onClick={() => {
                  setCurrentIndex(index);
                  handleIndicatorClick(indicator.link);
                }}
                className={`w-2 h-2 rounded-full transition-all duration-250 ease-out ${
                  index === currentIndex
                    ? 'bg-success-foreground w-6'
                    : 'bg-success-foreground/40 hover:bg-success-foreground/60'
                }`}
                aria-label={`View ${indicator.label}`}
              />
            ))}
          </div>

          <div className="flex items-center space-x-2 ml-4">
            <button
              onClick={() => setCurrentIndex((prev) => (prev - 1 + trustIndicators.length) % trustIndicators.length)}
              className="p-1 hover:bg-success-foreground/20 rounded transition-colors duration-250 ease-out"
              aria-label="Previous indicator"
            >
              <Icon name="ChevronLeftIcon" size={20} className="text-success-foreground" />
            </button>
            <button
              onClick={() => setCurrentIndex((prev) => (prev + 1) % trustIndicators.length)}
              className="p-1 hover:bg-success-foreground/20 rounded transition-colors duration-250 ease-out"
              aria-label="Next indicator"
            >
              <Icon name="ChevronRightIcon" size={20} className="text-success-foreground" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TrustIndicatorBar;